import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { App as AntApp, Input, Segmented } from 'antd';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import SheetDate from '../components/SheetDate';
import SheetSelect from '../components/SheetSelect';
import PartyQuickCreate from '../components/PartyQuickCreate';
import { AsyncButton } from '../components/kit';
import { AutosaveStatus, RestoredDraftBanner } from '../components/AutosaveStatus';
import usePagedOptions from '../hooks/usePagedOptions';
import useFormAutosave from '../hooks/useFormAutosave';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { buildLetterHtml, letterDocument } from '../lib/coAgencyLetterDoc';
import { SHARE_BASIS_OPTIONS } from '../lib/coAgencyShare';
import * as lettersApi from '../api/coAgencyLetters';
import * as contactsApi from '../api/contacts';

// FULL-SCREEN · /letters/new (create) and /letters/:id/edit (edit a draft). Structured
// like the booking form: sectioned cards, red asterisks driven by the same set submit()
// enforces, a contact picker that can create what it can't find, and a Preview that
// renders the real document.
//
// Nothing here is submitted to anyone. A letter is saved as the author's own draft and
// stays that way until they issue it from the detail screen — HQ verification was
// removed in 2026-08, along with the reject-and-resubmit loop that went with it.

const TYPE_OPTS = [{ label: 'Sale', value: 'SALES' }, { label: 'Rent', value: 'RENT' }];
const PRICE_OPTS = [{ label: 'Amount (RM)', value: 'AMOUNT' }, { label: 'Per sq ft', value: 'PSF' }];
const CREATE_NEW = '__create_new__';
// Co-agency picker page size — the sheet scrolls the next page in.
const CONTACT_PAGE = 20;

// Mandatory fields, mirroring the admin server's createLetterValidation (intersected
// with what this screen renders). Drives BOTH the asterisks and the submit checks, so
// the marks can never drift from the rules.
const REQUIRED = [
  ['letter_date', 'Date'],
  ['property_address', 'Property address'],
  ['co_agency_id', 'Co-agency'],
  ['co_agency_name', 'Co-agency name'],
  ['price_rm', 'Price'],
  ['tax_percentage', 'SST %'],
  ['co_agency_agent_name', 'Agent name'],
  ['co_agency_agent_contact', 'Agent contact'],
  ['co_agency_agent_ren_no', 'Agent REN no'],
];
// Per-sq-ft mode replaces the single price with its two components.
const REQUIRED_PSF = [['price_psf', 'Price per sq ft'], ['area_sqft', 'Area']];

const blank = (v) => v === undefined || v === null || String(v).trim() === '';

export default function LetterCreatePage() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const editing = !!editId;
  const { message } = AntApp.useApp();
  const { agent } = useAuth();

  const [loaded, setLoaded] = useState(!editing);
  const [letterRef, setLetterRef] = useState('');
  // An issued or voided letter is frozen — the same block the admin form renders.
  const [lockedStatus, setLockedStatus] = useState(null);
  // The loaded draft's updated_at — a device copy older than it is stale.
  const [serverUpdatedAt, setServerUpdatedAt] = useState(null);

  // One flat object keyed exactly like the API payload, so what's on screen is what's sent.
  const [f, setForm] = useState({
    letter_date: dayjs().format('YYYY-MM-DD'),
    letter_type: 'SALES',
    price_type: 'AMOUNT',
    tax_percentage: 8,
    // How the co-agency's cut is quoted: a ratio of the fee, or a flat sum with the
    // balance to CID. See lib/coAgencyShare.
    sharing_basis: 'PERCENTAGE',
    cid_sharing_ratio: 50,
    coa_sharing_ratio: 50,
    coa_sharing_amount: '',
  });
  // Every user edit goes through here, so it is also where autosave hears of it.
  const set = (patch) => {
    setForm((s) => ({ ...s, ...patch }));
    autosave.noteChange();
  };

  const [quickOpen, setQuickOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const isPsf = f.price_type === 'PSF';
  // Per-sq-ft mode computes the total the letter actually quotes; AMOUNT mode takes it directly.
  const computedRm = isPsf
    ? Number(((parseFloat(f.price_psf) || 0) * (parseFloat(f.area_sqft) || 0)).toFixed(2))
    : Number(f.price_rm || 0);

  const flatShare = f.sharing_basis === 'AMOUNT';
  const cidNum = Number(f.cid_sharing_ratio) || 0;
  const caNum = Number(f.coa_sharing_ratio) || 0;
  const caAmountNum = Number(f.coa_sharing_amount) || 0;
  const shareSum = cidNum + caNum;
  // A flat-sum letter has no ratios to balance — the co-agency takes the stated
  // amount and CID takes the balance of the fee. Only the percentage form has a
  // 100% to add up to.
  const balanced = flatShare ? caAmountNum > 0 : Math.abs(shareSum - 100) < 0.01;

  // Edit mode: load and prefill. Only a draft is editable.
  useEffect(() => {
    if (!editing) return;
    lettersApi.getCoAgencyLettersById(editId)
      .then((r) => {
        const d = r?.data ?? r;
        setLetterRef(d.letter_ref || '');
        setServerUpdatedAt(d.updated_at || null);
        if (!d.editable) setLockedStatus(d.status || d.status_raw);
        setForm({
          letter_date: d.letter_date ? dayjs(d.letter_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          letter_type: d.letter_type || 'SALES',
          property_address: d.property_address || '',
          co_agency_id: d.co_agency_id || null,
          co_agency_name: d.co_agency_name || '',
          co_agency_registration_no: d.co_agency_registration_no || '',
          co_agency_address: d.co_agency_address || '',
          co_agency_phone: d.co_agency_phone || '',
          price_type: d.price_type || 'AMOUNT',
          price_rm: d.price_rm != null ? String(d.price_rm) : '',
          price_psf: d.price_psf != null ? String(d.price_psf) : '',
          area_sqft: d.area_sqft != null ? String(d.area_sqft) : '',
          tax_percentage: d.tax_percentage != null ? d.tax_percentage : 8,
          co_agency_agent_name: d.co_agency_agent_name || '',
          co_agency_agent_contact: d.co_agency_agent_contact || '',
          co_agency_agent_ren_no: d.co_agency_agent_ren_no || '',
          co_agency_agent_email: d.co_agency_agent_email || '',
          sharing_basis: d.share_basis || 'PERCENTAGE',
          cid_sharing_ratio: d.cid_share != null ? d.cid_share : 50,
          coa_sharing_ratio: d.ca_share != null ? d.ca_share : 50,
          coa_sharing_amount: d.ca_share_amount != null ? String(d.ca_share_amount) : '',
        });
        setLoaded(true);
      })
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ── co-agency picker ──────────────────────────────────────────────────────
  // Paged: the registry is bigger than one screen, so the sheet scrolls the next
  // page in rather than stopping at whatever the first page happened to hold.
  // A failed page is swallowed by the hook — the lookup is best-effort here, since
  // the snapshot fields below still let the agent type the agency in by hand.
  const caLookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({ type: 'CO_AGENCY', q, page, limit: CONTACT_PAGE });
    return (r?.data ?? r ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      sub: [c.registration_no, c.contact_no].filter(Boolean).join(' · ') || undefined,
      data: c,
    }));
  }, CONTACT_PAGE);

  // Picking (or creating) a co-agency snapshots its details onto the letter: the
  // document quotes the agency as it was on the day, not as the registry reads later.
  const applyCoAgency = (c) => {
    if (!c?.id) return;
    set({
      co_agency_id: c.id,
      co_agency_name: c.name || '',
      co_agency_registration_no: c.registration_no || '',
      co_agency_address: c.address || '',
      co_agency_phone: c.contact_no || c.phone || '',
    });
  };

  // ── commission split ──────────────────────────────────────────────────────
  const clampPct = (v) => {
    const n = parseFloat(v);
    if (Number.isNaN(n)) return '';
    return Math.min(100, Math.max(0, n));
  };
  // Editing either share auto-completes the other (admin parity: 100 − value).
  const onCidShare = (v) => {
    const n = clampPct(v);
    set({ cid_sharing_ratio: n, ...(n !== '' ? { coa_sharing_ratio: 100 - n } : {}) });
  };
  const onCaShare = (v) => {
    const n = clampPct(v);
    set({ coa_sharing_ratio: n, ...(n !== '' ? { cid_sharing_ratio: 100 - n } : {}) });
  };
  // Switching basis clears the figure that no longer applies, so the draft can never
  // hold two shares and leave which one is the agreement to guess.
  const onBasis = (basis) => set(basis === 'AMOUNT'
    ? { sharing_basis: 'AMOUNT', coa_sharing_amount: '' }
    : { sharing_basis: 'PERCENTAGE', cid_sharing_ratio: 50, coa_sharing_ratio: 50, coa_sharing_amount: '' });

  // ── field renderers ───────────────────────────────────────────────────────
  const requiredKeys = new Set([
    ...REQUIRED.map(([k]) => k),
    ...(isPsf ? REQUIRED_PSF.map(([k]) => k) : []),
  ]);
  const isReq = (k) => requiredKeys.has(k);
  const T = (k, label, o = {}) => (
    <Field label={label} req={isReq(k)} key={k}>
      {o.textarea
        ? <Input.TextArea value={f[k] ?? ''} onChange={(e) => set({ [k]: e.target.value })} rows={o.rows || 2} placeholder={o.placeholder} />
        : <Input type={o.type} value={f[k] ?? ''} onChange={(e) => set({ [k]: e.target.value })} placeholder={o.placeholder} suffix={o.suffix} disabled={o.disabled} />}
    </Field>
  );
  const two = (a, b) => <div style={{ display: 'flex', gap: 12 }}><div style={{ flex: 1 }}>{a}</div><div style={{ flex: 1 }}>{b}</div></div>;

  // ── preview ───────────────────────────────────────────────────────────────
  // The real document: HQ's letterhead and body template from document_settings, with
  // the values on screen substituted in. Watermarked, because it isn't issued yet.
  const openPreview = async () => {
    const r = await lettersApi.listCoAgencyLetterSettings();
    const settings = r?.data ?? r ?? {};
    const draft = {
      ...f,
      letter_ref: letterRef || 'PENDING',
      price_rm: computedRm,
      share_basis: f.sharing_basis || 'PERCENTAGE',
      ca_share: caNum,
      cid_share: cidNum,
      ca_share_amount: flatShare ? caAmountNum : null,
      status_raw: 'PENDING',
    };
    setPreviewHtml(letterDocument(buildLetterHtml(draft, settings, agent), letterRef || 'Preview'));
  };

  // What stops this letter being saved, or null. One list for the Save button (which
  // says it) and for autosave (which quietly waits until there is nothing to say).
  const problem = () => {
    const missing = [
      ...REQUIRED,
      ...(isPsf ? REQUIRED_PSF : []),
    ].filter(([k]) => (k === 'price_rm' ? !computedRm : blank(f[k]))).map(([, label]) => label);
    if (missing.length) {
      const shown = missing.slice(0, 4).join(', ');
      return `Required: ${shown}${missing.length > 4 ? ` +${missing.length - 4} more` : ''}`;
    }
    if (!balanced) {
      return flatShare
        ? 'Enter the flat amount the co-agency is to receive.'
        : `Shares add up to ${shareSum}% — they must total 100%.`;
    }
    // Optional, but must be well-formed if given — the admin validator rejects it
    // server-side, and a typo here means the co-agency never receives the letter.
    const email = String(f.co_agency_agent_email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Agent email doesn’t look like a valid address';
    return null;
  };

  const buildPayload = () => ({
    letter_date: f.letter_date,
    letter_type: f.letter_type,
    property_address: String(f.property_address).trim(),
    co_agency_id: f.co_agency_id,
    co_agency_name: String(f.co_agency_name).trim(),
    co_agency_registration_no: String(f.co_agency_registration_no || '').trim() || null,
    co_agency_address: String(f.co_agency_address || '').trim() || null,
    co_agency_phone: String(f.co_agency_phone || '').trim() || null,
    price_type: f.price_type,
    price_rm: computedRm,
    ...(isPsf ? { price_psf: Number(f.price_psf || 0), area_sqft: Number(f.area_sqft || 0) } : {}),
    tax_percentage: Number(f.tax_percentage || 8),
    co_agency_agent_name: String(f.co_agency_agent_name).trim(),
    co_agency_agent_contact: String(f.co_agency_agent_contact).trim(),
    co_agency_agent_ren_no: String(f.co_agency_agent_ren_no).trim(),
    co_agency_agent_email: String(f.co_agency_agent_email || '').trim() || null,
    // One coherent share on the wire, never both halves at once — the form keeps
    // the hidden half's last value, and the server would only have to guess.
    sharing_basis: flatShare ? 'AMOUNT' : 'PERCENTAGE',
    coa_sharing_ratio: flatShare ? 0 : caNum,
    cid_sharing_ratio: flatShare ? 0 : cidNum,
    coa_sharing_amount: flatShare ? caAmountNum : null,
  });

  // Autosave, the owner's "C" (2026-10-02): a new letter is kept on this device
  // until Save draft; a saved draft (edit is PENDING-only) saves itself to the
  // server once it is complete enough to save at all. Same key as the desktop
  // LetterCreate, so rotating an iPad across the 1100px switch brings the typing
  // across.
  const [loadedTick, setLoadedTick] = useState(0);
  useEffect(() => { if (loaded && !lockedStatus) setLoadedTick((t) => t + 1); }, [loaded, lockedStatus]);
  const autosave = useFormAutosave({
    draftKey: `co-agency-letter:${editId || 'new'}`,
    userId: agent?.id,
    readValues: () => f,
    writeValues: (values) => setForm(values || {}),
    serverSave: editing && loaded && !lockedStatus
      ? () => lettersApi.updateCoAgencyLettersById(editId, buildPayload())
      : undefined,
    canServerSave: () => !problem(),
    serverUpdatedAt,
    loadedTick,
  });
  // A copy typed while this letter was still a draft is no use once it is issued.
  useEffect(() => { if (lockedStatus) autosave.discardDevice(); }, [lockedStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Save the draft. Returns the saved letter, or null (having said why). Throws on an API error. */
  const saveLetter = async () => {
    const why = problem();
    if (why) { message.warning(why); return null; }
    const token = autosave.changeToken();
    const r = editing
      ? await lettersApi.updateCoAgencyLettersById(editId, buildPayload())
      : await lettersApi.createCoAgencyLetters(buildPayload());
    autosave.markSaved(token);
    return (r?.data ?? r) || { id: editId };
  };

  const guard = useUnsavedChanges(autosave.dirty, {
    what: 'this co-agency letter',
    keptOnDevice: !autosave.toServer,
    onSave: async () => {
      try {
        return !!(await saveLetter());
      } catch (e) {
        message.error(e.message);
        return false;
      }
    },
    saveLabel: 'Save draft & leave',
    onDiscard: autosave.discardDevice,
  });

  const submit = async () => {
    const saved = await saveLetter();
    if (!saved) return;
    message.success(editing ? 'Draft updated' : 'Draft saved — issue it when the terms are settled');
    guard.release();
    // Straight to the letter it just made, where Issue is: the list gives no clue
    // that the next move is the author's own.
    nav(saved.id ? `/letters/${saved.id}` : '/letters');
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title={editing ? 'Edit letter' : 'New letter'}
        sub={editing ? 'update the draft' : 'saved as a draft you issue yourself'}
      />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {editing && !loaded ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
        ) : lockedStatus ? (
          <div style={{ ...card, gap: 12 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>This letter is {String(lockedStatus).toLowerCase()} and can no longer be edited.</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Only a draft can be changed — an issued letter's terms are the terms.</div>
            <AsyncButton block onClick={async () => nav(`/letters/${editId}`)}>Back to the letter</AsyncButton>
          </div>
        ) : (
          <>
            <RestoredDraftBanner autosave={autosave} onServer={editing} notYet="It is not a draft yet — Save draft keeps it." />
            <div style={card}>
              <Field label="Letter type" req>
                <Segmented block options={TYPE_OPTS} value={f.letter_type} onChange={(v) => set({ letter_type: v })} />
              </Field>
              <Field label="Date" req>
                <SheetDate
                  title="Letter date"
                  placeholder="Pick the letter date"
                  value={f.letter_date ? dayjs(f.letter_date) : null}
                  onChange={(d) => set({ letter_date: d ? d.format('YYYY-MM-DD') : undefined })}
                />
              </Field>
            </div>

            <div style={sectionLabel}>CO-AGENCY</div>
            <div style={card}>
              <Field label="Co-agency" req>
                <SheetSelect
                  title="Pick a co-agency"
                  placeholder="Search & pick…"
                  searchPlaceholder="Search by name or registration no…"
                  value={f.co_agency_id || undefined}
                  selectedLabel={f.co_agency_name}
                  onSearch={caLookup.search}
                  onChange={(cid) => {
                    if (cid === CREATE_NEW) { setQuickOpen(true); return; }
                    applyCoAgency(caLookup.options.find((o) => o.value === cid)?.data);
                  }}
                  options={[
                    { value: CREATE_NEW, label: <span style={{ color: 'var(--primary)', fontWeight: 600 }}>＋ Create new co-agency…</span> },
                    ...caLookup.options,
                  ]}
                  onLoadMore={caLookup.loadMore}
                  hasMore={caLookup.hasMore}
                  loadingMore={caLookup.loadingMore}
                  notFoundContent="No co-agency matches — use ＋ Create new above."
                />
              </Field>
              {/* Snapshot fields — prefilled from the registry, editable because the
                  letter records the agency as it was when it was signed. */}
              {T('co_agency_name', 'Co-agency name', { placeholder: 'Registered name' })}
              {T('co_agency_registration_no', 'Registration no', { placeholder: 'e.g. 201901012345' })}
              {T('co_agency_address', 'Address', { textarea: true, placeholder: 'Mailing address' })}
              {T('co_agency_phone', 'Tel', { placeholder: '03-1234 5678' })}
            </div>

            <div style={sectionLabel}>DEAL</div>
            <div style={card}>
              {T('property_address', 'Property address', { textarea: true, placeholder: 'Full property address' })}
              <Field label="Price basis">
                <Segmented block options={PRICE_OPTS} value={f.price_type} onChange={(v) => set({ price_type: v })} />
              </Field>
              {isPsf ? (
                <>
                  {two(
                    T('price_psf', 'Price (RM/psf)', { type: 'number', placeholder: '20' }),
                    T('area_sqft', 'Area (sq ft)', { type: 'number', placeholder: '1000' }),
                  )}
                  <Field label="Total (RM)">
                    <Input disabled value={computedRm ? computedRm.toLocaleString('en-MY', { minimumFractionDigits: 2 }) : ''} placeholder="0.00" />
                  </Field>
                </>
              ) : (
                T('price_rm', 'Price (RM)', { type: 'number', placeholder: '500000' })
              )}
              <Field label="SST %" req>
                <Input type="number" value={f.tax_percentage ?? ''} onChange={(e) => set({ tax_percentage: clampPct(e.target.value) })} suffix="%" />
              </Field>
            </div>

            <div style={sectionLabel}>CO-AGENCY AGENT</div>
            <div style={card}>
              {T('co_agency_agent_name', 'Agent name', { placeholder: 'Handling agent name' })}
              {two(
                T('co_agency_agent_contact', 'Contact (H/P)', { placeholder: '012-345 6789' }),
                T('co_agency_agent_ren_no', 'REN no', { placeholder: 'REN 12345' }),
              )}
              {T('co_agency_agent_email', 'Email (optional)', { type: 'email', placeholder: 'agent@email.com' })}
            </div>

            <div style={sectionLabel}>COMMISSION SPLIT</div>
            <div style={card}>
              <Segmented
                block
                value={f.sharing_basis || 'PERCENTAGE'}
                onChange={onBasis}
                options={SHARE_BASIS_OPTIONS}
              />
              {flatShare ? (
                <>
                  <Field label="Co-agency gets (RM)" req>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={f.coa_sharing_amount}
                      onChange={(e) => set({ coa_sharing_amount: e.target.value })}
                      prefix="RM"
                      placeholder="5000.00"
                    />
                  </Field>
                  {/* No CID field: with the co-agency's cut fixed, CID's is the rest of
                      whatever fee is actually received. A second figure could only
                      disagree with it. */}
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
                    CID Realtors takes the balance of the professional fee.
                  </div>
                  {!balanced && (
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--warn)' }}>
                      Enter the flat amount the co-agency is to receive.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>CID Realtors · <b>{cidNum}%</b></span>
                    <span><b>{caNum}%</b> · {f.co_agency_name || 'Co-agency'}</span>
                  </div>
                  <input
                    type="range" min={10} max={90} step={5}
                    value={Math.min(90, Math.max(10, cidNum))}
                    onChange={(e) => onCidShare(e.target.value)}
                    style={{ width: '100%', accentColor: 'var(--primary)' }}
                  />
                  <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, Math.max(0, cidNum))}%`, background: 'var(--primary)' }} />
                    <div style={{ flex: 1, background: 'var(--chip)' }} />
                  </div>
                  {two(
                    <Field label="CID Realtors %" req>
                      <Input type="number" value={f.cid_sharing_ratio} onChange={(e) => onCidShare(e.target.value)} suffix="%" />
                    </Field>,
                    <Field label="Co-agency %" req>
                      <Input type="number" value={f.coa_sharing_ratio} onChange={(e) => onCaShare(e.target.value)} suffix="%" />
                    </Field>,
                  )}
                  {!balanced && (
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--warn)' }}>
                      Shares add up to {shareSum}% — they must total 100%.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Auto reference — assigned server-side on submission; real ref when editing */}
            <div style={{ background: 'var(--chip)', borderRadius: 12, padding: '14px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12.5, color: 'var(--primary)', fontWeight: 500 }}>Reference (auto)</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', fontFamily: 'ui-monospace,monospace', whiteSpace: 'nowrap' }}>
                {editing ? (letterRef || '—') : 'DPSB-CA ··-·····'}
              </span>
            </div>
          </>
        )}
      </div>

      <PartyQuickCreate
        open={quickOpen}
        type="coagency"
        defaultSub="CO_AGENCY"
        onClose={() => setQuickOpen(false)}
        onCreated={(c) => { applyCoAgency(c); setQuickOpen(false); }}
      />

      {!lockedStatus && (
        <div style={{ padding: '10px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AutosaveStatus
            autosave={autosave}
            idleText="A draft stays private until you issue it"
            savedLabel={`Saved to draft ${letterRef}`}
            waitingText="Fill in the * fields to start saving automatically"
            style={{ textAlign: 'center' }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <AsyncButton onClick={openPreview} onError={(e) => message.error(e.message)} style={{ flex: 'none', paddingInline: 18 }}>
              Preview
            </AsyncButton>
            <AsyncButton type="primary" block onClick={submit} onError={(e) => message.error(e.message)}>
              {editing ? 'Save changes' : 'Save draft'}
            </AsyncButton>
          </div>
        </div>
      )}

      {previewHtml && (
        <DocViewer
          title={`Preview · ${letterRef || 'Co-agency letter'}`}
          html={previewHtml}
          onClose={() => setPreviewHtml(null)}
        />
      )}
    </div>
  );
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginBottom: -6, marginTop: 2 };
// `req` renders the admin-portal-style red asterisk on mandatory fields.
const Field = ({ label, req, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </label>
    {children}
  </div>
);
